import {useEffect, useMemo, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {useQuery} from '@apollo/client';
import {useAtomValue, useSetAtom} from 'jotai';
import dayjs from 'dayjs';
import {
   DailyWellReport_AllWhereDocument,
   RunTicket_AllWhereDocument,
   Well_AllDocument,
   User_GetMyWellsDocument,
} from '@/graphql/generated/graphql.ts';
import {toWell} from '@/lib/wellMapping';
import {toRunTicket, isAwaitingReview} from '@/lib/runTicketMapping';
import type {RunTicket, Well} from '@/types/well';
import {routerLinks} from '@/Constants.ts';
import {MAX_BACKDATE_DAYS} from '@/pages/wells/dailyReportTypes.ts';
import {authStatusAtom, cachedSessionAtom, isOnlineAtom} from './authAtoms';
import {useCognitoReachable} from './useCognitoReachable';
import {isFieldRole} from '@/types/auth';
import {pushNotificationAtom} from './notificationAtoms';
import {
   runTicketsAtom,
   runTicketsLoadingAtom,
   runTicketsErrorAtom,
   unmatchedReportsAtom,
   unmatchedTicketIdsAtom,
   pendingRunTicketFocusAtom,
   type UnmatchedReport,
} from './runTicketAtoms';

// How far back to look for Daily Reports when cross-referencing ticket
// numbers. DailyWellReportSearchInput's entryDate filter is an exact-match
// list (no "since"/range operator on this schema), so there's no way to
// ask for "the last N days" directly — this just builds that list of
// exact date strings instead.
//
// Deliberately capped to MAX_BACKDATE_DAYS rather than some longer,
// independent window: a Daily Report/Run Ticket mismatch only gets
// flagged here because it's something the user is expected to go fix
// (add the missing run ticket, backdate the report, etc.), and
// MAX_BACKDATE_DAYS is the actual limit on how far back a Daily Report
// can be edited/backdated. Looking further back than that would surface
// a warning the user has no way to act on — it would just sit there
// permanently instead of getting resolved or aging out. A mismatch older
// than this is a stale data problem to clean up separately, not
// something worth an active nudge today.
const RECONCILIATION_WINDOW_DAYS = MAX_BACKDATE_DAYS;

// UPDATED 2026-09-12 (Duane: "back off the frequency to 5 minutes. This
// would only be checking if another user made a change, but I'm guessing
// that won't happen very frequently. The frequent check was needed more
// for run tickets that the photo was scanned, but now most if not all of
// those will happen in the attach photo"). Both queries below started at
// a 30s poll (docs/run-ticket-scan-spec.md) back when a scanned ticket's
// scanStatus transition to awaiting-review was something this poll was
// the only way to notice promptly. That's no longer true: the
// 2026-09-10 RunTicket_AttachPhoto rework (see project memory
// runticket_mobile_ux_and_offline_list_2026_09_10.md, section 11) made
// the photo-attach step itself return the scanned ticket's full data the
// moment the AI scan completes, and every real save now explicitly
// refetches both queries below on demand (RunTicket_AllWhere after a
// ticket save/delete; DailyWellReport_AllWhere after a report save — see
// each mutation call site's own refetchQueries) rather than waiting on
// the interval. What's left for this poll to catch is only a change made
// by ANOTHER user/device — a different pumper's ticket or report — which
// is genuinely infrequent, so a much longer interval is enough to still
// feel current without the constant background traffic.
const RECONCILIATION_POLL_MS = 5 * 60 * 1000; // 5 minutes

// REMOVED 2026-08-21 per Duane: a VARIANCE_TOLERANCE_BBLS constant and a
// real Balanced/Variance quantity check used to live here (SRS 3.8, added
// just the day before) — diffing a matched ticket's nsvBarrels against
// its Daily Report's oilBarrelRuns. The client decided there's no good
// way to calculate a useful variance: pumpers don't record how much a
// tank was drawn down for shipping, tanks often take pumping from
// multiple wells, and a tank isn't always emptied completely — so a
// computed mismatch couldn't mean anything reliable. The run ticket
// itself is the final authority; the pumper's own review of it is the
// only check now. This hook still does the Unmatched cross-reference
// below (does any Daily Report even cite this ticket's number) — that's
// a data-entry consistency check, not a quantity variance, and the
// client didn't ask for it to go away.

// How long to hold off the "no matching Daily Report" notification for
// a ticket that came from a photo scan and hasn't finished being
// reviewed — Duane confirmed 2026-08-20. A scanned ticket often shows up
// unmatched for a little while simply because the AI scan/review hasn't
// caught up yet, not because anything is actually wrong; without this,
// every scan produced an "Unmatched" toast followed shortly after by a
// "ready to review" toast for the very same ticket. Manually-entered
// tickets (scanStatus null — no photo, nothing to wait on) still notify
// immediately, no grace period — see notifiableUnmatchedIds below.
//
// This is wall-clock time from the first poll that confirms (while
// connected) the ticket has no matching report — NOT "15 minutes of
// online time." In practice that ends up being the right thing either
// way: a ticket scanned while offline doesn't exist on the server yet,
// so it can't be flagged unmatched — and this clock can't start — until
// it's synced, which requires being back online first. The AI scan job
// itself runs server-side once the photo's uploaded, so it keeps
// progressing whether or not the client stays connected afterward.
const UNMATCHED_SCAN_GRACE_MS = 15 * 60 * 1000;

// scanStatus values meaning "still in the scan/review pipeline, not
// settled yet" — see the grace period above. 'Approved'/'Failed'/null
// ('NotStarted') are all settled: either reviewed already, or there
// was never a scan to wait on in the first place, so those tickets get
// the ordinary immediate treatment.
//
// 'Scanned' was added here 2026-08-25 — CORRECTED from the 2026-08-24
// assumption that it meant "scan completed cleanly, nothing to flag,
// treat as settled like 'Approved'". Duane confirmed the opposite: real
// tickets awaiting review carry scanStatus 'Scanned', not 'NeedsReview'
// — see isAwaitingReview's doc comment in runTicketMapping.ts for the
// full correction. A 'Scanned' ticket is exactly as mid-pipeline as a
// 'NeedsReview' one, so it belongs here too.
const SCAN_IN_FLIGHT_STATUSES = new Set(['Pending', 'Processing', 'NeedsReview', 'Scanned']);

function recentDateStrings(days: number): string[] {
   return Array.from({length: days}, (_, i) => dayjs().subtract(i, 'day').format('YYYY-MM-DD'));
}

/**
 * Runs globally (mounted once in App.tsx, like useAuthBootstrap/
 * useOfflineSync) so the Run Tickets nav badge and the notification
 * nudges both work no matter what page the user is actually on — not
 * just while RunTicketsPage itself happens to be mounted.
 *
 * Cross-references runTicketsAtom (kept in sync with the real
 * RunTicket_AllWhere query below — see runTicketAtoms.ts) against a real
 * DailyWellReport_AllWhere query in both directions:
 *   - a RunTicket whose ticketNumber no data report references (→ that
 *     ticket's displayed status becomes 'Unmatched', see
 *     unmatchedTicketIdsAtom)
 *   - a DailyWellReport whose runTicketNumber has no matching RunTicket
 *     at all (→ unmatchedReportsAtom, surfaced as an alert on
 *     RunTicketsPage since there's no dedicated Daily Reports list page)
 *
 * A third case — a RunTicket that IS matched, diffing nsvBarrels against
 * the matched report's oilBarrelRuns for a Balanced/Variance quantity
 * check — briefly lived here (SRS 3.8, added 2026-08-20) and was removed
 * 2026-08-21 per Duane: no good way to calculate a useful variance given
 * pumpers don't record tank draw-down for shipping. See runTicketAtoms.ts.
 *
 * IMPORTANT, clarified 2026-08-24 (Duane): the ticketNumber cross-check
 * above isn't just a data-entry typo catcher — it's also the mechanism
 * that surfaces a genuine AI-scan discrepancy. The real backend scan job
 * (confirmed live, see types/well.ts's RunTicket.ticketNumber doc
 * comment and docs/run-ticket-scan-spec.md) overwrites a ticket's
 * ticketNumber with whatever it reads off the photo, which can end up
 * different from what the pumper originally typed into the matching
 * Daily Report's own runTicketNumber field. When that happens, THIS is
 * the check that flags it (→ 'Unmatched'), not a separate
 * scanned-vs-entered comparison living somewhere else. Reviewing a
 * 'NeedsReview' ticket (RunTicketFormPanel) is where a human is meant to
 * catch and resolve which side was actually wrong.
 *
 * Also watches runTicketsAtom for scanStatus becoming awaiting-review
 * (isAwaitingReview — 'NeedsReview' or, as corrected 2026-08-25, the
 * real-world 'Scanned'). Pushes a one-click "Review" notification (see
 * notificationAtoms.ts)
 * for anything *newly* detected on a given poll — deliberately not for
 * whatever was already true the first time this runs, so opening the
 * app doesn't immediately dump every pre-existing issue as a toast; the
 * durable indicators (RunTicketsPage's chips/alert, the nav badge) are
 * what surface those instead.
 *
 * The Unmatched toast specifically also holds off for
 * UNMATCHED_SCAN_GRACE_MS on a ticket that came from a photo scan and
 * hasn't been reviewed yet (see SCAN_IN_FLIGHT_STATUSES below) — added
 * 2026-08-20 after a freshly-scanned ticket produced an "Unmatched"
 * toast immediately followed by a "ready to review" toast for the exact
 * same ticket, which read as two separate problems rather than one
 * ticket still working its way through the pipeline. The durable
 * Unmatched chip/count is NOT delayed the same way — only this toast.
 */

/**
 * RESOLVED 2026-08-26 (Duane): a field User should NOT see or be
 * notified about Run Tickets/Daily Reports on wells that aren't assigned
 * to them — same standing rule as every well picker/filter in this app
 * (well_scoping_user_wells.md), now extended to this hook, which had
 * been the one place it was flagged but not yet applied. Both real
 * queries below (RunTicket_AllWhere, DailyWellReport_AllWhere) are
 * filtered to the signed-in field User's own wells server-side, via
 * each query's own wellId search arg (see assignedWellIds/fieldUser
 * below). UPDATED 2026-08-26 (Duane): RunTicket_All had no way to bound
 * the search, unlike DailyWellReport_AllWhere — switched to
 * RunTicket_AllWhere (RunTicket_All.graphql deleted) so a field User's
 * ticket fetch is scoped server-side too, not just client-side filtered
 * after an unbounded fetch. Since runTicketsAtom itself only ever
 * contains a field User's own-well tickets now, everything downstream —
 * RunTicketsPage's table/chips/CSV export, needsReviewCountAtom's nav
 * badge, AND this hook's own toast notifications — is correctly scoped
 * for free, with no separate filtering needed at any of those call
 * sites. Admin/SuperAdmin: unaffected, still see everything everywhere
 * (assignedWellIds stays null for them, so runTicketSearch is omitted
 * entirely — same "null means unrestricted" convention as the
 * DailyWellReport query below).
 */
export function useRunTicketReconciliation() {
   const tickets = useAtomValue(runTicketsAtom);
   const setTickets = useSetAtom(runTicketsAtom);
   const setTicketsLoading = useSetAtom(runTicketsLoadingAtom);
   const setTicketsError = useSetAtom(runTicketsErrorAtom);
   const setUnmatchedReports = useSetAtom(unmatchedReportsAtom);
   const setUnmatchedTicketIds = useSetAtom(unmatchedTicketIdsAtom);
   const setPendingFocus = useSetAtom(pendingRunTicketFocusAtom);
   const pushNotification = useSetAtom(pushNotificationAtom);
   const navigate = useNavigate();
   const location = useLocation();

   // Which wells this signed-in user is allowed to reconcile/be notified
   // about — RESOLVED 2026-08-26 (Duane): a field User should see and be
   // notified about Run Tickets/Daily Reports for their own assigned
   // wells only, same standing rule as every well picker/filter in this
   // app (see well_scoping_user_wells.md) — this hook's own well-name
   // lookup was the one place that rule had been flagged but not yet
   // applied, pending that decision. Admin/SuperAdmin still see
   // everything (assignedWellIds stays null for them, same "null means
   // unrestricted" convention runTicketAtoms.ts and this file's inScope-
   // style filters use elsewhere in this app).
   //
   // user_GetMyWells already does the Admin/SuperAdmin-sees-everything
   // vs. User-sees-only-assigned split server-side (see
   // well_scoping_user_wells.md), so for a field User it doubles as both
   // the well-name lookup AND the assigned-well-id set below — no
   // separate query needed. Admin/SuperAdmin keep using well_All (the
   // wider, unrestricted lookup every other well is resolved against).
   const cachedSession = useAtomValue(cachedSessionAtom);
   const fieldUser = cachedSession != null && isFieldRole(cachedSession.role);
   // FIXED 2026-08-28 (Duane, via the backend developer): "the frontend
   // code is attempting to make gql requests before the user logs in
   // which obviously error out" — AWS saw a burst of 4xx responses
   // immediately after deploy, which can itself affect the deployment
   // health check. Root cause: this hook is mounted globally in
   // App.tsx's AppInner, OUTSIDE of RequireAuth, specifically so the Run
   // Tickets nav badge/notifications work no matter what page someone is
   // on — but that also means it was running (and its 30s polls kept
   // running) whenever the app was mounted at all, including on the
   // login page before signing in, and even after signing out (nothing
   // here previously depended on auth status, only on fieldUser, which
   // just defaults to `false` whenever there's no cached session at all
   // — that's "not a field user" and "not logged in" both looking
   // identical to this hook). Every query below now also waits on
   // authStatus being genuinely signed in — same allow-list RequireAuth
   // itself uses ('authenticated' or 'offline-cached'; 'loading' and
   // 'unauthenticated' both skip) — so nothing fires until there's
   // actually a session to attach a token to, and everything stops
   // firing again the moment a sign-out flips authStatus back to
   // 'unauthenticated'.
   const authStatus = useAtomValue(authStatusAtom);
   const isAuthed = authStatus === 'authenticated' || authStatus === 'offline-cached';
   // ADDED 2026-09-10, follow-up to offline_sync_auth_reachability_backoff_2026_09_04.md:
   // that fix stopped the offline MUTATION QUEUE (useOfflineSync.ts) from
   // replaying into a not-yet-reachable Cognito on reconnect, but this
   // hook's own two 30s-polling READ queries below (RunTicket_AllWhere,
   // DailyWellReport_AllWhere) were never covered by it -- their `skip`
   // only checked `isAuthed` above (a cached/local session flag) and
   // never online state at all, so the poll kept ticking straight through
   // being offline (apolloClient.ts's timeoutLink still lets a
   // watchQuery's network leg fire unconditionally, just with a shorter
   // ceiling), and resumed the instant the device came back online with
   // no confirmation Cognito itself was reachable yet -- the residual
   // DailyWellReport_AllWhere 400s Duane was still seeing after the
   // mutation-queue fix. Both queries below now also skip while offline
   // and stay skipped until useCognitoReachable() confirms a real token
   // is obtainable again (same exponential-backoff shape as
   // useOfflineSync.ts's own check, kept as an independent hook -- see
   // useCognitoReachable.ts's own comment).
   const isOnline = useAtomValue(isOnlineAtom);
   const cognitoReachable = useCognitoReachable(isOnline);
   const {data: allWellsData, loading: allWellsLoading} = useQuery(Well_AllDocument, {
      errorPolicy: 'all',
      skip: !isAuthed || fieldUser,
   });
   const {data: myWellsData, loading: myWellsLoading} = useQuery(User_GetMyWellsDocument, {
      errorPolicy: 'all',
      skip: !isAuthed || !fieldUser,
   });
   const wellsLoading = fieldUser ? myWellsLoading : allWellsLoading;
   const wells = useMemo(() => {
      const rows = fieldUser ? (myWellsData?.user_GetMyWells ?? []) : (allWellsData?.well_All ?? []);
      return rows.map((row) => toWell(row)).filter((w): w is Well => w !== null);
   }, [fieldUser, myWellsData, allWellsData]);
   const wellNameById = useMemo(() => new Map(wells.map((w) => [w.id, w.wellName])), [wells]);
   // null = unrestricted (Admin/SuperAdmin) — see the comment above.
   const assignedWellIds = useMemo(() => (fieldUser ? new Set(wells.map((w) => w.id)) : null), [fieldUser, wells]);

   // entryDates/pickupDateStart/pickupDateEnd — shared RECONCILIATION_WINDOW_DAYS
   // window, computed once here so both real queries below (RunTicket_AllWhere
   // and DailyWellReport_AllWhere) stay bound to the exact same range. entryDates
   // itself is still the full day-by-day list (needed below — entryDateSet backs
   // the unreported-report-gap check); entryDates[0] is always today and
   // entryDates[entryDates.length - 1] the oldest day in the window (see
   // recentDateStrings above), so those two values bound the contiguous range
   // for both queries.
   //
   // pickupDateStart/pickupDateEnd ADDED 2026-09-18 per Duane: "Somewhere there
   // is a RunTicket_AllWhere running without parameters. There needs to always
   // be a date range." See RunTicket_AllWhere.graphql's own header comment
   // for the full rationale on why this query now always carries a bound.
   //
   // FIXED 2026-09-25 (same root cause as RunTicketsPage.tsx's identical fix,
   // same day — see that file's comment for the full story): this used to be
   // built the way Ogp03iRunTicket_AllWhere.graphql's pickupDateStart/
   // pickupDateEnd is — explicit UTC day-boundary strings appended directly to
   // entryDates' calendar-date strings. That's correct for Ogp03i because its
   // one consumer (computeOilBarrelsRuns) buckets pickupDate by a UTC date
   // slice. It's wrong here: entryDates are LOCAL calendar dates (see
   // recentDateStrings above), and the ticketsInWindow filter below re-derives
   // its own local calendar date from each ticket's pickupDate
   // (`dayjs(t.pickupDate).format('YYYY-MM-DD')`) to compare against
   // entryDateSet — i.e. this hook's real bucketing is already local-date
   // based end to end. Appending a literal "Z" to a local calendar-date
   // string silently treated it as already UTC, so the server-side fetch
   // bound (unlike the client-side entryDateSet check) cut off the last few
   // hours of "today" for anyone west of Greenwich — a ticket picked up after
   // ~6pm local (Mountain Time) could be dropped by the query before
   // ticketsInWindow ever saw it, even though its local pickupDate was
   // correctly inside the window. dayjs(dateString) parses a date-only
   // "YYYY-MM-DD" string as local midnight (not UTC, unlike native Date), so
   // converting through dayjs and letting it do the local->UTC conversion
   // fixes this the same way RunTicketsPage.tsx's fix does.
   const entryDates = useMemo(() => recentDateStrings(RECONCILIATION_WINDOW_DAYS), []);
   const pickupDateStart = dayjs(entryDates[entryDates.length - 1])
      .startOf('day')
      .toISOString();
   const pickupDateEnd = dayjs(entryDates[0]).endOf('day').toISOString();

   // Real RunTicket_AllWhere query — the sole owner of runTicketsAtom's
   // contents (see runTicketAtoms.ts). Same RECONCILIATION_POLL_MS poll
   // as the DailyWellReport query below, so this hook's other effect
   // (needsReview/unmatched detection) keeps reacting to fresh data
   // without a separate refetch mechanism. Deleted tickets are filtered
   // out here rather than carried into the atom — there's no "show
   // deleted run tickets" UI, unlike Wells' active/inactive toggle.
   //
   // Bound to the same RECONCILIATION_WINDOW_DAYS pickupDate window as the
   // DailyWellReport query below (see pickupDateStart/pickupDateEnd above) —
   // this query used to run with no date bound at all, fetching every run
   // ticket ever created. That's also exactly right for this hook's own
   // purposes: the Unmatched cross-check and Needs Review badge only ever
   // needed to reason about recent tickets in the first place (see
   // RECONCILIATION_WINDOW_DAYS's own comment).
   const {
      data: ticketsData,
      loading: ticketsLoading,
      error: ticketsError,
   } = useQuery(RunTicket_AllWhereDocument, {
      variables: {
         // Field User: scoped server-side to their own wells — see the
         // RESOLVED/UPDATED 2026-08-26 comment above. Admin/SuperAdmin:
         // omitted entirely (assignedWellIds null), unrestricted as
         // before.
         runTicketSearch: assignedWellIds != null ? {wellId: [...assignedWellIds]} : undefined,
         pickupDateStart,
         pickupDateEnd,
      },
      // Waits for the field User's own well list to resolve before
      // running — assignedWellIds briefly being an empty Set on first
      // mount (before myWellsData loads) would otherwise fire this query
      // once with an empty wellId list instead of the real one. Same
      // reasoning as the DailyWellReport query below.
      skip: !isAuthed || !isOnline || !cognitoReachable || (fieldUser && wellsLoading),
      pollInterval: RECONCILIATION_POLL_MS,
      errorPolicy: 'all',
   });

   // Published so pages that read runTicketsAtom without running this
   // query themselves (RunTicketsPage, AuditReportPage) can still show
   // an accurate DataStatusBanner instead of guessing from the atom's
   // contents alone (an empty array is ambiguous between "still loading"
   // and "genuinely no tickets").
   useEffect(() => {
      setTicketsLoading(ticketsLoading);
   }, [ticketsLoading, setTicketsLoading]);
   useEffect(() => {
      setTicketsError(ticketsError ?? null);
   }, [ticketsError, setTicketsError]);

   // REMOVED 2026-08-17: this used to bail out (`if
   // (!ticketsData?.runTicket_AllWhere) return;`) before ever calling
   // setTickets, which is what let the SAMPLE_RUN_TICKETS seed (see
   // runTicketAtoms.ts) survive indefinitely on any settled error or
   // before the first successful response — including offline on first
   // load, with no cache yet. runTicketsAtom now starts empty, and this
   // effect unconditionally reflects whatever the query actually has:
   // real tickets once it resolves, empty if it resolves with none, and
   // (via Apollo's own previousData retention) the last real data during
   // a transient poll error — same as every other query-backed list in
   // this app.
   useEffect(() => {
      const mapped = (ticketsData?.runTicket_AllWhere ?? [])
         .filter((row): row is NonNullable<typeof row> => row != null && !row.isDeleted)
         .map((row) => toRunTicket(row, wellNameById))
         .filter((t): t is RunTicket => t !== null)
         // Redundant with the server-side wellId filter on the query
         // above for a field User (the query never returns an
         // out-of-scope ticket in the first place), but kept as a
         // defense-in-depth backstop — cheap, and it's what already made
         // every downstream reader (RunTicketsPage's table/chips/CSV
         // export, needsReviewCountAtom's nav badge, this hook's own
         // toast notifications below) correctly scoped with no risk of
         // one of them being missed. Admin/SuperAdmin: assignedWellIds is
         // null, so this is a no-op.
         .filter((t) => assignedWellIds === null || assignedWellIds.has(t.wellId));
      setTickets(mapped);
   }, [ticketsData, wellNameById, assignedWellIds, setTickets]);

   // entryDates (and the QUERY scoping via entryDateStart/entryDateEnd — a
   // real >=/<= range filter the backend added 2026-09-15, see
   // DailyWellReport_AllWhere.graphql's header comment) computed once,
   // above, alongside pickupDateStart/pickupDateEnd — see that comment.
   const {data: reportsData} = useQuery(DailyWellReport_AllWhereDocument, {
      variables: {
         entryDateStart: entryDates[entryDates.length - 1],
         entryDateEnd: entryDates[0],
         dailyWellReportSearch: {
            // Field User: scoped server-side to their own wells, same
            // reasoning as the ticket filter above — a field User's
            // Unmatched-report alert/toast should never reference a well
            // that isn't theirs either. Admin/SuperAdmin: omitted
            // entirely (assignedWellIds null), unrestricted as before.
            ...(assignedWellIds != null ? {wellId: [...assignedWellIds]} : {}),
         },
      },
      // Waits for the field User's own well list to resolve before
      // running — assignedWellIds briefly being null on first mount
      // (before myWellsData loads) would otherwise fire this query once
      // unscoped for everyone, defeating the whole point.
      skip: !isAuthed || !isOnline || !cognitoReachable || (fieldUser && wellsLoading),
      // Simple pull/poll, not a subscription — same reasoning as the
      // Needs Review query in docs/run-ticket-scan-spec.md: needs zero
      // new transport (Apollo's own pollInterval, no websocket/
      // subscription setup). See RECONCILIATION_POLL_MS's own comment
      // above for why this is now 5 minutes rather than the original 30s.
      pollInterval: RECONCILIATION_POLL_MS,
      errorPolicy: 'all',
   });

   // null until the first computation completes — lets the effect below
   // tell "nothing to compare against yet" apart from "genuinely empty,"
   // which is what makes the "don't notify on first load" rule work.
   const seenNeedsReviewIds = useRef<Set<string> | null>(null);
   const seenUnmatchedTicketIds = useRef<Set<string> | null>(null);
   const seenUnmatchedReportIds = useRef<Set<string> | null>(null);
   // When each currently-unmatched ticket id was FIRST confirmed
   // unmatched — see UNMATCHED_SCAN_GRACE_MS above. Not persisted
   // anywhere (in-memory only, like the seen*Ids refs above) — a reload
   // just restarts the clock, same tradeoff those already make.
   const unmatchedFirstSeenAt = useRef<Map<string, number>>(new Map());

   useEffect(() => {
      // wellNameById only ever has entries once its query (Well_AllDocument
      // or, for a field User, User_GetMyWellsDocument) resolves —
      // before that it's an empty Map, so every wellId lookup below would
      // fall back to 'Unknown well'. That's fine for the durable
      // unmatchedReportsAtom (it just recomputes with real names once
      // wellsLoading flips false, since wellNameById is a dependency
      // below), but pushNotification snapshots the message text at the
      // moment it fires — a one-shot toast that fired mid-race would be
      // permanently stuck reading "Unknown well's Daily Report...".
      // Waiting for wells here means the very first real run always has
      // real names, so that can't happen.
      if (wellsLoading) return;

      // All three toasts below have a durable equivalent already visible
      // on RunTicketsPage itself (the Needs Review/Unmatched chips, and
      // the unmatched-reports Alert) — see the matching comments on each.
      // Someone already looking at that page doesn't need a popup on top
      // of what they can already see live; someone elsewhere in the app
      // does, since that's the only heads-up they'd get otherwise.
      const onRunTicketsPage = location.pathname === routerLinks.runTickets;

      const reports = (reportsData?.dailyWellReport_AllWhere ?? []).filter(
         (r): r is NonNullable<typeof r> => r != null && !r.isDeleted,
      );
      const reportTicketNumbers = new Set(reports.map((r) => r.runTicketNumber).filter((n): n is string => Boolean(n)));
      const ticketNumbers = new Set(tickets.map((t) => t.ticketNumber));

      // FIXED 2026-08-31 (Duane): "the unmatched number of run tickets in
      // the notifications doesn't always have the correct number" —
      // reproduced by code reading, not live (this session couldn't keep
      // a dev server up long enough — see project memory). Root cause:
      // `reports` above is scoped to entryDates (the last
      // RECONCILIATION_WINDOW_DAYS days — see that constant's own doc
      // comment), but `tickets` was never scoped the same way — it's
      // every RunTicket this user can see, however old. A ticket whose
      // real matching report is, say, 9 days old (past a 7-day window)
      // used to flip to 'Unmatched' the moment that report aged out of
      // `reportTicketNumbers`, even though a genuine match exists — the
      // client just stopped fetching it. That's a false positive, not
      // the "old, unactionable mismatch" the window was meant to retire
      // (see RECONCILIATION_WINDOW_DAYS's own comment: the intent was
      // never to flag NEW problems as tickets age, only to stop
      // re-surfacing OLD ones) — and since which tickets happen to sit
      // right at that rolling edge changes day to day, this read as
      // "sometimes right, sometimes wrong" rather than a constant offset.
      // Fixed by scoping which tickets get evaluated to the same window,
      // via the one date field a RunTicket actually carries
      // (`pickupDate`) — a ticket outside the window is simply excluded
      // from this check entirely (neither matched nor unmatched), same
      // as an old report past the window already was.
      const entryDateSet = new Set(entryDates);
      const ticketsInWindow = tickets.filter((t) => entryDateSet.has(dayjs(t.pickupDate).format('YYYY-MM-DD')));

      const unmatchedTicketIds = new Set(
         ticketsInWindow.filter((t) => t.ticketNumber && !reportTicketNumbers.has(t.ticketNumber)).map((t) => t.id),
      );
      setUnmatchedTicketIds(unmatchedTicketIds);

      // REMOVED 2026-08-21 per Duane — see the doc comment above this
      // hook and runTicketAtoms.ts for why: this used to diff each
      // matched ticket's nsvBarrels against its report's oilBarrelRuns
      // and set a Balanced/Variance status via ticketReconciliationAtom.

      const unmatchedReports: UnmatchedReport[] = reports
         .filter((r) => r.runTicketNumber && !ticketNumbers.has(r.runTicketNumber))
         .map((r) => ({
            reportId: String(r.id),
            wellId: String(r.wellId),
            wellName: wellNameById.get(String(r.wellId)) ?? 'Unknown well',
            entryDate: String(r.entryDate).slice(0, 10),
            runTicketNumber: String(r.runTicketNumber),
         }));
      setUnmatchedReports(unmatchedReports);

      // isAwaitingReview, not a raw `=== 'NeedsReview'` check — corrected
      // 2026-08-25 per Duane, who confirmed real tickets awaiting review
      // carry scanStatus 'Scanned'. See runTicketMapping.ts.
      const needsReviewIds = new Set(tickets.filter((t) => isAwaitingReview(t.scanStatus)).map((t) => t.id));

      if (seenNeedsReviewIds.current) {
         const newCount = [...needsReviewIds].filter((id) => !seenNeedsReviewIds.current!.has(id)).length;
         // Skipped on Run Tickets itself — the "Needs Review: N" chip
         // there already shows this live.
         //
         // FIXED 2026-08-29 (Duane, reported for the Unmatched toast
         // below — same bug, same fix, applied here too since it's the
         // identical pattern): the message used to report `newCount` —
         // how many tickets became reviewable SINCE THE LAST POLL — not
         // how many there actually are right now. Clicking "Review" sets
         // quickFilters to 'needsReview', which filters RunTicketsPage's
         // table against the FULL needsReviewIds set, not just the ones
         // that triggered this particular toast. Those two numbers only
         // ever coincide by accident (e.g. the very first batch, with
         // nothing pre-existing) — any time some were already sitting
         // there awaiting review before this poll, or some got resolved
         // between the toast firing and the click, the toast's own
         // number stops matching what's actually shown, exactly the "the
         // notification should match the real number" complaint. The
         // trigger condition (newCount > 0 — only pop a toast when
         // something NEW actually appeared, so this doesn't re-announce
         // the same static backlog every 30s poll forever) is unchanged;
         // only the number displayed switches to the true current total.
         if (newCount > 0 && !onRunTicketsPage) {
            const total = needsReviewIds.size;
            pushNotification({
               message: total === 1 ? '1 run ticket is ready to review.' : `${total} run tickets are ready to review.`,
               // FIXED 2026-08-29 (Duane, follow-up): see the `kind`
               // field's own doc comment in notificationAtoms.ts — this
               // `message` is now only the initial/fallback text;
               // NotificationSnackbar recomputes the live count from
               // needsReviewCountAtom on every render instead of trusting
               // this frozen string, so a toast that sits queued for a
               // while (auto-hide is 8s, but the queue itself doesn't
               // expire) never goes stale relative to what RunTicketsPage
               // itself would show right now.
               kind: 'needsReview',
               actionLabel: 'Review',
               severity: 'success',
               onAction: () => {
                  setPendingFocus('needsReview');
                  navigate(routerLinks.runTickets);
               },
            });
         }
      }
      seenNeedsReviewIds.current = needsReviewIds;

      // Bookkeeping for the grace period: start the clock the first time
      // an id shows up unmatched, forget it once it's no longer
      // unmatched (matched later, deleted, etc.) so a ticket that goes
      // unmatched again later gets a fresh 15 minutes rather than
      // reusing a stale timestamp from a previous mismatch.
      const now = Date.now();
      for (const id of unmatchedFirstSeenAt.current.keys()) {
         if (!unmatchedTicketIds.has(id)) unmatchedFirstSeenAt.current.delete(id);
      }
      for (const id of unmatchedTicketIds) {
         if (!unmatchedFirstSeenAt.current.has(id)) unmatchedFirstSeenAt.current.set(id, now);
      }

      // The notification-eligible subset of unmatchedTicketIds — the
      // durable Unmatched chip/count above already reflects the FULL
      // set immediately and unconditionally; this filter only delays
      // the interruptive toast, per UNMATCHED_SCAN_GRACE_MS.
      const notifiableUnmatchedIds = new Set(
         [...unmatchedTicketIds].filter((id) => {
            const t = tickets.find((ticket) => ticket.id === id);
            const scanInFlight = t?.scanStatus != null && SCAN_IN_FLIGHT_STATUSES.has(t.scanStatus);
            if (!scanInFlight) return true; // manually entered (no photo) or already reviewed — notify right away
            const firstSeen = unmatchedFirstSeenAt.current.get(id) ?? now;
            return now - firstSeen >= UNMATCHED_SCAN_GRACE_MS;
         }),
      );

      if (seenUnmatchedTicketIds.current) {
         const newCount = [...notifiableUnmatchedIds].filter((id) => !seenUnmatchedTicketIds.current!.has(id)).length;
         // Skipped on Run Tickets itself — the "Unmatched: N" chip there
         // already shows this live.
         //
         // FIXED 2026-08-29 (Duane): "I get the message that 16 run
         // tickets are unmatched. When I click on the review button,
         // only 4 run tickets are shown. The notification should match
         // the real number of unmatched run tickets." Root cause: the
         // message displayed `newCount` — tickets that newly crossed into
         // notifiable-unmatched SINCE THE LAST POLL (and, for a
         // scan-in-flight ticket, only after clearing
         // UNMATCHED_SCAN_GRACE_MS on top of that) — not the actual
         // current count. Clicking "Review" sets quickFilters to
         // 'unmatched', which filters RunTicketsPage's table against the
         // FULL current unmatchedTicketIds set (also excluding whatever
         // has since been fixed or aged past the reconciliation window),
         // which is a different number from a delta accumulated over
         // however many polls came before it. The two only matched by
         // coincidence before; now the message always states the same
         // total the filtered view is about to show. The trigger
         // condition (newCount > 0 — only interrupt when something NEW
         // actually became unmatched, not every 30s poll for the same
         // static backlog) is unchanged; only the displayed number now
         // reads the true current total instead of the delta.
         if (newCount > 0 && !onRunTicketsPage) {
            const total = unmatchedTicketIds.size;
            pushNotification({
               message:
                  total === 1
                     ? '1 run ticket has no matching Daily Report.'
                     : `${total} run tickets have no matching Daily Report.`,
               // FIXED 2026-08-29 (Duane, follow-up — "filter shows 5, the
               // notification says 20"): the earlier same-day fix made
               // this show the true total AT PUSH TIME, but `message` is
               // a plain string that never changes again once queued —
               // the real backlog keeps moving every 30s poll, so a toast
               // left unread for even a couple of polls can go stale
               // relative to reality by the time it's actually seen. See
               // `kind`'s doc comment in notificationAtoms.ts:
               // NotificationSnackbar now recomputes this message fresh
               // from unmatchedTicketIdsAtom on every render, so whatever
               // is on screen always matches RunTicketsPage's own
               // "Unmatched: N" chip at that exact instant, not a
               // snapshot from whenever this was pushed.
               kind: 'unmatchedTickets',
               actionLabel: 'Review',
               severity: 'warning',
               onAction: () => {
                  setPendingFocus('unmatched');
                  navigate(routerLinks.runTickets);
               },
            });
         }
      }
      seenUnmatchedTicketIds.current = notifiableUnmatchedIds;

      const unmatchedReportIds = new Set(unmatchedReports.map((u) => u.reportId));
      if (seenUnmatchedReportIds.current) {
         const newOnes = unmatchedReports.filter((u) => !seenUnmatchedReportIds.current!.has(u.reportId));
         // Skipped while already on Run Tickets — RunTicketsPage's own
         // Alert banner shows this exact same well/ticket detail (see the
         // matching comment there) and is already visible, so the toast
         // would just be a duplicate popup on top of it. Off that page,
         // this is still the only heads-up that a mismatch exists at all.
         //
         // FIXED 2026-08-29 (Duane, same bug/fix as the two toasts
         // above): this used to report `newOnes.length` — reports that
         // newly became unmatched since the last poll — while
         // RunTicketsPage's own Alert (the "View"/"Go to Report" action
         // this toast's own "Review" click leads to) always shows the
         // FULL current `unmatchedReports.length`. Switched to the total
         // so the toast's number always matches what's actually waiting
         // on that page. Trigger condition unchanged (still only pops
         // when something new actually appeared); the singular-vs-plural
         // and single-item detail text below also now key off the total
         // rather than the delta, for the same reason.
         if (newOnes.length > 0 && !onRunTicketsPage) {
            const total = unmatchedReports.length;
            pushNotification({
               message:
                  total === 1
                     ? `${unmatchedReports[0].wellName}'s Daily Report references ticket #${unmatchedReports[0].runTicketNumber} — no matching Run Ticket yet.`
                     : `${total} Daily Reports reference a run ticket number with no matching Run Ticket.`,
               // FIXED 2026-08-29 (Duane, follow-up) — same fix/reasoning
               // as the other two toasts above: `message` is now only the
               // fallback text. NotificationSnackbar recomputes this live
               // from unmatchedReportsAtom (including which specific
               // report/ticket to name in the singular case) on every
               // render, so it can never go stale relative to
               // RunTicketsPage's own Alert banner, no matter how long
               // this sits queued before being seen.
               kind: 'unmatchedReports',
               actionLabel: 'Review',
               severity: 'warning',
               onAction: () => navigate(routerLinks.runTickets),
            });
         }
      }
      seenUnmatchedReportIds.current = unmatchedReportIds;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [tickets, reportsData, wellNameById, wellsLoading, location.pathname]);
}
